import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DashboardT } from 'business/definitions';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { snackbarService } from 'shared/lib/snackbar/service';
import { RQAPICallWithData } from 'store/APIClient';

import { useGetDashboardFromSlugOrMetadata } from './useGetDashboardFromSlugOrMetadata';

const useDuplicateChart = (
    onUpdateSuccess?: (key: number, updatedProps: Partial<DashboardT>) => void,
) => {
    const navigate = useNavigate();
    const currentDashboard = useGetDashboardFromSlugOrMetadata();
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: async ({
            widgetKey,
            dashboardSlug,
            fromPosition,
        }: {
            widgetKey: number;
            dashboardSlug: string;
            fromPosition?: FromPositionQueryT;
        }) => {
            const response = await RQAPICallWithData<{
                dashboard: DashboardT;
                newWidgetKey: number;
            }>({
                endpoint: `explore/widget/dashboard/${widgetKey}/duplicate/${dashboardSlug}`,
                method: 'POST',
                body: { previousWidgetPosition: fromPosition },
            });
            return response.content.data;
        },

        onSuccess: async (data, { widgetKey, dashboardSlug }) => {
            const { dashboard, newWidgetKey } = data;
            if (onUpdateSuccess) onUpdateSuccess(widgetKey, dashboard);
            const targetUrl = `/dashboard/${dashboardSlug}#widget-${newWidgetKey}`;
            const isDuplicatingOnCurrentDashboard =
                currentDashboard?.slug === dashboardSlug;
            if (isDuplicatingOnCurrentDashboard) {
                await queryClient.invalidateQueries({
                    queryKey: ['getLayout', dashboardSlug, ''],
                });
                navigate(targetUrl);
            }
            snackbarService.showSuccess('', {
                message: t('components:common.see'),
                onClick: () => {
                    navigate(targetUrl);
                },
            });
        },

        onError: (error: Error) => {
            snackbarService.showError(error.message);
        },
    });
};

export default useDuplicateChart;
